import React, { PropsWithChildren, useEffect } from "react";
import "./modal.css";
import { useTranslation } from "react-i18next";

interface ModalProps {
  id: string;
  open: boolean;
  title?: string | React.ReactNode;
  confirm?: string;
  onCloseModal: () => void;
  hasClose: boolean;
  onConfirm?: () => void;
  actionButtons?: React.ReactNode;
}

export function Modal({
  id,
  open,
  title,
  children,
  confirm,
  onCloseModal,
  hasClose,
  actionButtons,
  onConfirm,
  ...rest
}: PropsWithChildren<ModalProps>) {
  const { t } = useTranslation();

  useEffect(() => {
    const dialog = document.getElementById(id) as HTMLDialogElement;
    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open, id]);

  // Sync React state when dialog closes natively (e.g., escape key)
  useEffect(() => {
    const dialog = document.getElementById(id) as HTMLDialogElement;
    const handleClose = () => {
      if (open) {
        onCloseModal();
      }
    };
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [id, open, onCloseModal]);

  return (
    <dialog id={id} className="modal is-active docket-modal" {...rest}>
      <div className="modal-box text-base-content">
        <form method="dialog">
          <div className="modal-card-head">
            {title && (
              <p className="modal-card-title font-bold text-xl">
                <b>{title}</b>
              </p>
            )}
            {hasClose && (
              <button
                className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                onClick={onCloseModal}
                data-testid="close-modal"
              >
                ✕
              </button>
            )}
          </div>
        </form>
        <div className="has-text-black">{children}</div>
        <div className="modal-action">
          {onConfirm && (
            <button className={"btn px-4"} onClick={onConfirm}>
              {confirm || t("generic.ok")}
            </button>
          )}
          {actionButtons && actionButtons}
        </div>
      </div>
    </dialog>
  );
}
